/**
 * Route-change usage tracking (issue #1451, part of epic #1448).
 *
 * Wires the single TanStack Router instance's `onLoad` lifecycle event
 * (`frontend/src/router.tsx`) to a `page.viewed` usage event, batched
 * through `usageEventBatch.ts#trackEvent`.
 *
 * `onLoad` — not `onBeforeNavigate` — is the event that fires once a
 * navigation has actually committed (`@tanstack/router-core`'s
 * `load-client.js`); `preloadClientRoute` (triggered by `defaultPreload:
 * 'intent'` in `router.tsx`, e.g. a link hover) is a separate code path that
 * never emits `onLoad`, so hovering/preloading a link never fires a
 * `page.viewed` on its own — only a committed navigation does.
 *
 * A committed navigation can still resolve to the same location it started
 * from (e.g. a redirect chain that lands back where it began, or the router
 * re-settling after an aborted transition) — `wireRouteChangeTracking` guards
 * against re-firing for an unchanged pathname so a `page.viewed` maps 1:1 to
 * an actual route change, per the issue's "without double-firing on
 * redirects" acceptance criterion.
 */

import { trackEvent } from './usageEventBatch';

export interface RouteChangeEvent {
  toLocation: {
    pathname: string;
  };
}

export interface RouteChangeRouter {
  subscribe(eventType: 'onLoad', fn: (event: RouteChangeEvent) => void): () => void;
}

/**
 * Subscribes to the router's `onLoad` event and emits a `page.viewed` usage
 * event per distinct pathname. Returns the router's unsubscribe function.
 */
export function wireRouteChangeTracking(router: RouteChangeRouter): () => void {
  let lastTrackedPath: string | null = null;

  return router.subscribe('onLoad', (event) => {
    const path = event.toLocation.pathname;
    if (path === lastTrackedPath) return;
    lastTrackedPath = path;
    trackEvent('page.viewed', { path });
  });
}
